import { Component, Children, cloneElement } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import lodash from 'lodash';
import styles from './StudyContainer.scss';
import { getStudy, unloadStudy, updateStudy } from 'pages/studyOverview/actions/study.js';
import { getTaskCount } from 'pages/studyOverview/actions/tasks.js';
import StudyNavigation from 'pages/studyOverview/containers/StudyNavigation/StudyNavigation';
import { LoadingStatus } from 'components/common/LoadingStatus/LoadingStatus';
import { LIFECYCLE_UNASSIGNED } from 'common/util/studyHelpers';
import { studyEventFeatureAccess, doaFeatureAccess } from 'common/features/predicates';
import { canAccessStudyPermissions, actions, features } from 'common/permissions/studyPredicates';
import { userCanAccessStudyDelegation, manageStudyTasksPredicate } from 'common/permissions/studyPredicates';
import { newDoaStudyFlowAccess, loadHomePageV4Access } from '../../../../common/features/predicates';
import { requestBinderExport } from '../../../../common/actions/binderExportActions';

export class StudyContainer extends Component {
    constructor(props) {
        super(props);

        this.state = {
            fixedNav: false,
            eventsTabEnabled: props.eventsTabEnabled,
            delegationOfAuthorityEnabled: props.delegationOfAuthorityEnabled
        };

        this.calculateFixedNav = this.calculateFixedNav.bind(this);
    }

    componentDidMount() {
        const {
            getStudy,
            studyId,
        } = this.props;
        getStudy(studyId);
        window.addEventListener('scroll', this.calculateFixedNav);
    }



    componentDidUpdate(prevProps) {
        const {
            study,
            studyPermissions,
            binderId,
            studyId,
            getTaskCount,
            eventsTabEnabled,
            delegationOfAuthorityEnabled,
            canManageStudyTasks,
            isNewDoaEnabled
        } = this.props;

        const { router } = this.context;

        // Check if study has an error (permission denied)
        if (study && study.error === 'ACCESS_DENIED') {
            if (router) {
                router.replace('/');
            }
            return;
        }

        // Check if study loaded but user has no permissions
        if (study && study.id && !prevProps.study) {
            if (!studyPermissions || studyPermissions.length === 0) {
                if (router) {
                    router.replace('/');
                }
                return;
            }
        }

        if (location.pathname.includes('/delegation') && isNewDoaEnabled) {
            window.removeEventListener('scroll', this.calculateFixedNav);
        }
        if (binderId && canManageStudyTasks && !lodash.isEqual(prevProps.canManageStudyTasks, canManageStudyTasks)) {
            getTaskCount(binderId, studyId, 'pending');
            getTaskCount(binderId, studyId, 'completed');

            this.setState(() => ({ taskCount: this.props.taskCount, }));
        }

        if (!lodash.isEqual(prevProps.eventsTabEnabled, eventsTabEnabled)) {
            this.setState({ eventsTabEnabled });
        }
        if (!lodash.isEqual(prevProps.delegationOfAuthorityEnabled, delegationOfAuthorityEnabled)) {
            this.setState({ delegationOfAuthorityEnabled });
        }
    }


    componentWillUnmount() {
        const { unloadStudy } = this.props;
        unloadStudy();
        window.removeEventListener('scroll', this.calculateFixedNav);
    }

    calculateFixedNav() {
        const { fixedNav } = this.state;
        const TOP_NAVIGATION_HEIGHT_REM = 3.5;
        const currentPageOffsetRem = window.pageYOffset / 16;
        const hasScrolledPastTopNav = currentPageOffsetRem > TOP_NAVIGATION_HEIGHT_REM;

        if (hasScrolledPastTopNav && !fixedNav) {
            return this.setFixedNav(true);
        }

        if (!hasScrolledPastTopNav && fixedNav) {
            return this.setFixedNav(false);
        }
    }

    setFixedNav(isFixed) {
        this.setState({
            fixedNav: isFixed
        });
    }

    /**
     * Checks if core binder fields have loaded
     * before rendering child components.
     */
    binderIsFullyAvailable(binder) {
        return !lodash.isEmpty(binder) &&
            !lodash.isNil(binder.title) &&
            !lodash.isNil(binder.layout);
    }

    render() {
        const {
            study,
            studySettings,
            studyStage,
            studyPermissions,
            binder,
            taskCount,
            studyStageProgressions,
            complion4FlagEnabled,
        } = this.props;

        // Show loading if study exists but permissions haven't loaded yet
        if (!study || !this.binderIsFullyAvailable(binder) || studyPermissions === undefined) {
            return <LoadingStatus itemName={complion4FlagEnabled ? 'Binder' : 'Study'} loading={true} filterQuery={false} />;
        }


        const childElement = cloneElement(Children.only(this.props.children), {
            study,
            studySettings,
            studyStage,
            binder,
            taskCount,
            fixedNav: this.state.fixedNav,
            updateStudy,
            studyStageProgressions,
        });

        return <StudyTemplate props={this.props} childElement={childElement} fixedNav={this.state.fixedNav} eventsTabEnabled={this.state.eventsTabEnabled} delegationOfAuthorityEnabled={this.state.delegationOfAuthorityEnabled} />;
    }
}

StudyContainer.contextTypes = {
    router: PropTypes.object.isRequired
};

export const StudyTemplate = ({ props, childElement, fixedNav, eventsTabEnabled, delegationOfAuthorityEnabled }) => {
    const {
        router,
        location,
        study,
        studyStage,
        studyType,
        studyPermissions,
        hasLifecycle,
        isStudyEvent,
        isdoaFeatureAccess,
        isDoaEnabled,
        isNewDoaEnabled,
        complion4FlagEnabled
    } = props;

    const insertIf = (condition, ...elements) => {
        return condition ? elements : [];
    };

    const getTabs = (studyId) => {
        const mainPath = `/study/${studyId}`;
        const tabs = [
            {
                label: 'Dashboard',
                path: `${mainPath}/dashboard`,
                permissions: [
                    { action: actions.VIEW, feature: features.DASHBOARD },
                ],
            },
            {
                label: 'Documents',
                path: `${mainPath}/documents`,
                permissions: [],
            },
            {
                label: 'Expectations',
                path: `${mainPath}/expectations`,
                permissions: [
                    { action: actions.VIEW, feature: features.EXPECTATIONS },
                    { action: actions.MANAGE, feature: features.EXPECTATIONS },
                ],
            },
            ...insertIf(isStudyEvent && eventsTabEnabled, {
                label: 'Events',
                path: `${mainPath}/events`,
                permissions: [
                    { action: actions.VIEW, feature: features.EVENTS },
                    { action: actions.MANAGE, feature: features.EVENTS },
                ],
            }),
            ...insertIf(isNewDoaEnabled, {
                label: 'Users',
                path: `${mainPath}/users`,
                permissions: [
                    { action: actions.VIEW, feature: features.USERS },
                    { action: actions.MANAGE, feature: features.USERS },
                    { action: actions.VIEW, feature: features.MONITORS },
                    { action: actions.MANAGE, feature: features.MONITORS },
                    { action: actions.VIEW, feature: features.TEAM },
                    { action: actions.MANAGE, feature: features.TEAM },
                ],
            }),
            ...insertIf(
                isDoaEnabled && (complion4FlagEnabled ? (delegationOfAuthorityEnabled && isdoaFeatureAccess) : true),
                {
                    label: 'Delegation',
                    path: `${mainPath}/delegation`,
                    permissions: [
                        { action: actions.VIEW, feature: features.DELEGATION },
                        { action: actions.MANAGE, feature: features.DELEGATION },
                    ],
                }
            ),
            {
                label: 'Tasks',
                path: `${mainPath}/tasks`,
                permissions: [
                    { action: actions.MANAGE, feature: features.TASKS },
                ],
            },
            {
                label: 'Settings',
                path: `${mainPath}/settings`,
                permissions: [
                    { action: actions.VIEW, feature: features.METADATA },
                    { action: actions.MANAGE, feature: features.METADATA },
                    { action: actions.MANAGE, feature: features.EXPECTATIONS },
                    { action: actions.VIEW, feature: features.USERS },
                    { action: actions.MANAGE, feature: features.USERS },
                    { action: actions.VIEW, feature: features.TEAM },
                    { action: actions.MANAGE, feature: features.TEAM },
                    { action: actions.VIEW, feature: features.MONITORS },
                    { action: actions.MANAGE, feature: features.MONITORS }
                ],
            }
        ].filter(tab => {
            if (lodash.isEmpty(tab.permissions)) {
                return true;
            }
            return tab.permissions.reduce(
                (prev, current) => prev || canAccessStudyPermissions(studyPermissions, current.action, current.feature),
                false
            );
        });
        return tabs;
    };

    const overviewContainerChildStyle = fixedNav ? styles.stuckOverviewContainerChild : styles.OverviewContainerChild;

    return (
        <div className={styles.OverviewContainer}>
            <StudyNavigation
                title={study.name}
                tabs={getTabs(study.id)}
                stage={studyStage.name}
                type={studyType}
                lifecycle={hasLifecycle}
                router={router}
                location={location}
                fixedNav={fixedNav}
                hasMaxWidth={true}
                complion4FlagEnabled={complion4FlagEnabled}
                binderId={props.binderId}
                requestBinderExport={props.requestBinderExport}
            />
            <div className={overviewContainerChildStyle}>
                {childElement}
            </div>
        </div>
    );
};

export default connect((state, props) => {
    const binder = lodash.get(state, 'binder', null);
    const binderId = lodash.get(state, 'study.binderId', null);
    const study = lodash.get(state, 'study', null);
    const studyId = lodash.get(props, 'params.id', (study ? study.id : null));
    const studySettings = lodash.get(state, 'studySettings', null);
    const studyStageProgressions = lodash.get(state, 'study.studyStageProgressions', []);
    const studyStage = lodash.get(state, 'study.currentStage', { name: LIFECYCLE_UNASSIGNED }) || { name: LIFECYCLE_UNASSIGNED };
    const studyType = lodash.get(state, 'study.studyType.name', null);
    const hasLifecycle = (!!studyStage.name && studyStage.name !== LIFECYCLE_UNASSIGNED);
    const taskCount = {
        pending: lodash.get(state, 'study.tasks.pending.count', 0),
        completed: lodash.get(state, 'study.tasks.completed.count', 0),
    };
    const studyPermissions = lodash.get(state, 'me.studyPermissions', {});
    const isDoaEnabled = userCanAccessStudyDelegation(state);
    const isStudyEvent = studyEventFeatureAccess(state);
    const isdoaFeatureAccess = doaFeatureAccess(state);
    const isNewDoaEnabled = newDoaStudyFlowAccess(state);
    const eventsTabEnabled = lodash.get(study, 'eventsTabEnabled', false);
    const delegationOfAuthorityEnabled = lodash.get(study, 'delegationOfAuthorityEnabled', false);
    const canManageStudyTasks = manageStudyTasksPredicate(state);
    const complion4FlagEnabled = loadHomePageV4Access(state);

    return {
        binder,
        binderId,
        study,
        studyId,
        studyStageProgressions,
        studySettings,
        studyStage,
        studyType,
        hasLifecycle,
        taskCount,
        isStudyEvent,
        isdoaFeatureAccess,
        studyPermissions,
        isDoaEnabled,
        eventsTabEnabled,
        delegationOfAuthorityEnabled,
        canManageStudyTasks,
        isNewDoaEnabled,
        complion4FlagEnabled
    };
}, {
    getStudy,
    getTaskCount,
    unloadStudy,
    updateStudy,
    requestBinderExport,
})(StudyContainer);